import { getPhysicalIOLabel } from '../Utils/physical_io_label';
import React, { useState } from 'react';
import MuiSelect from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { isMobile } from 'react-device-detect';
import { makeStyles } from '@material-ui/core/styles';
import {
  alarmMapping,
  multiOutMapping,
  multiIOMapping,
  multiIOHwTypeMappingSelect,
  multiMultiIOHwTypeMappingSelect,
  multiIOHwTypeMappping,
  getValveIrrigationRouteCount,
  MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE,
  eventMapping,
  resolutions
} from '../Constants/constants';

import { isZonable, isZoned } from '../Utils/utils.js';
import { IsFirmwareVersionSelectable } from '../Utils/firmware_utils.js';

import { useTranslation } from 'react-i18next';

function isGrowBaseProWifiLocalIrrigationOutput(controller, outputIndex) {
  return (
    controller?.type === 5055 &&
    Boolean(controller?.settings?.system?.esp32_firmware_name) && // TODO (fux): Why is this needed?
    Number(outputIndex) >= 1 &&
    Number(outputIndex) <= 5
  );
}

const useStyles = makeStyles((theme) => ({
  selectEmpty: {
    minWidth: 120,

    '& .MuiSelect-select': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    color: theme.palette.fontcolor
  },
  selectIndex: {
    minWidth: 40,
    color: theme.palette.fontcolor
  },
  label: {
    color: theme.palette.primary.main
  },
  unsavedSelect: {
    '&&:after': {
      borderBottom: `2px solid ${theme.palette.secondary.main}`,
      transform: 'scaleX(1)'
    }
  }
}));

export function selectValuesMatch(value, controllerValue) {
  if (Array.isArray(value) || Array.isArray(controllerValue)) {
    return (
      Array.isArray(value) &&
      Array.isArray(controllerValue) &&
      value.length === controllerValue.length &&
      value.every((item, index) => selectValuesMatch(item, controllerValue[index]))
    );
  }

  if (
    value === null ||
    value === undefined ||
    controllerValue === null ||
    controllerValue === undefined
  ) {
    return value === controllerValue;
  }

  return String(value) === String(controllerValue);
}

export function Select({ controller_value, className = '', ...other }) {
  const classes = useStyles();
  const tracksControllerValue = controller_value !== undefined;
  const unsaved = tracksControllerValue && !selectValuesMatch(other.value, controller_value);

  return (
    <MuiSelect
      className={`${className} ${unsaved ? classes.unsavedSelect : ''}`.trim()}
      data-unsaved={unsaved ? 'true' : undefined}
      {...other}
    />
  );
}

export function EnableDisableSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('disable')}</MenuItem>
      <MenuItem value={1}>{t('enable')}</MenuItem>
    </Select>
  );
}
export function OffOnSettings({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('off')}</MenuItem>
      <MenuItem value={1}>{t('on')}</MenuItem>
    </Select>
  );
}

export function SwitchSettings({ value, onChange, ...other }) {
  let { t } = useTranslation();
  return (
    <FormControlLabel control={<Switch onChange={onChange} {...other} />} label={t('on_off')} />
  );
}

export function ApplyDeleteSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('auto_save_delete')}</MenuItem>
      <MenuItem value={1}>{t('auto_save_paste')}</MenuItem>
    </Select>
  );
}

export function DisplayTimeoutSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('displ_timeout_60')}</MenuItem>
      <MenuItem value={1}>{t('displ_timeout_100')}</MenuItem>
      <MenuItem value={2}>{t('displ_timeout_160')}</MenuItem>
      <MenuItem value={3}>{t('displ_timeout_on')}</MenuItem>
    </Select>
  );
}

export function EnableDayTimeDisableSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('disable')}</MenuItem>
      <MenuItem value={1}>{t('enable_at_daytime')}</MenuItem>
    </Select>
  );
}

export function EnableDisableDaytimeSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('disable')}</MenuItem>
      <MenuItem value={1}>{t('enable_always')}</MenuItem>
      <MenuItem value={2}>{t('enable_at_daytime')}</MenuItem>
    </Select>
  );
}
export function AbsoluteRelativeSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('relative')}</MenuItem>
      <MenuItem value={1}>{t('absolute')}</MenuItem>
    </Select>
  );
}

export function RoomScalseSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('small')}</MenuItem>
      <MenuItem value={1}>{t('medium')}</MenuItem>
      <MenuItem value={2}>{t('large')}</MenuItem>
    </Select>
  );
}

export function CylinderValveSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={1}>{t('co2_cylinder_valve_1')}</MenuItem>
      <MenuItem value={2}>{t('co2_cylinder_valve_2')}</MenuItem>
    </Select>
  );
}

export function CylinderStatusSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('empty')}</MenuItem>
      <MenuItem value={1}>{t('nonempty')}</MenuItem>
    </Select>
  );
}

export function EcAcSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('show_ac_ec')}</MenuItem>
      <MenuItem value={1}>{t('show_ec')}</MenuItem>
      <MenuItem value={2}>{t('show_ac')}</MenuItem>
    </Select>
  );
}

export function DehumidificationModeSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('off')}</MenuItem>
      <MenuItem value={1}>{t('low')}</MenuItem>
      <MenuItem value={2}>{t('medium')}</MenuItem>
      <MenuItem value={3}>{t('high')}</MenuItem>
    </Select>
  );
}

export function RegTypeCo2Select({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('fuzzy')}</MenuItem>
      <MenuItem value={1}>{t('P')}</MenuItem>
      <MenuItem value={2}>{t('PD')}</MenuItem>
    </Select>
  );
}

export function RegTypeSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('PD')}</MenuItem>
      <MenuItem value={1}>{t('P')}</MenuItem>
    </Select>
  );
}

export function RegTypeHumidifierSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('PD')}</MenuItem>
      <MenuItem value={1}>{t('P')}</MenuItem>
      <MenuItem value={2}>{t('PID')}</MenuItem>
    </Select>
  );
}

export function TempFormatSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('celsius')}</MenuItem>
      <MenuItem value={1}>{t('fahrenheit')}</MenuItem>
    </Select>
  );
}

export function VPDFormatSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('hectopascal')}</MenuItem>
      <MenuItem value={1}>{t('kilopascal')}</MenuItem>
    </Select>
  );
}

export function DateFormatSelect({ ...other }) {
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>YYYY-MM-DD</MenuItem>
      <MenuItem value={1}>DD.MM.YYYY</MenuItem>
    </Select>
  );
}

export function LanguageSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('english')}</MenuItem>
      <MenuItem value={1}>{t('german')}</MenuItem>
    </Select>
  );
}

export function AverageMinMaxSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('average')}</MenuItem>
      <MenuItem value={1}>{t('min')}</MenuItem>
      <MenuItem value={2}>{t('max')}</MenuItem>
    </Select>
  );
}

export function MultiOutSelect({ type, controller, parent, port, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  const outputIndex = port?.id ?? other.outputIndex ?? other.name;

  // Only GB Pro outs with index 1-5 have irrigation options
  const showGrowBaseProWifiIrrigation = isGrowBaseProWifiLocalIrrigationOutput(
    controller,
    outputIndex
  );

  // TODO (fux): this seems not correct! Remove all industrial-related code !!
  let not_pro = ![5055, 6000].includes(controller.type);

  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem disabled={type === 1 || type === 2} value={1}>
        {t('intake_fans_ac')}
      </MenuItem>
      <MenuItem disabled={type === 1 || type === 2} value={0}>
        {t('exhaust_fans_ac')}
      </MenuItem>

      {controller.type >= 6000 /*&& port.type == 'AO_VOLTAGE'*/ && (
        <MenuItem value={100}>{t('intake_fans_ec')}</MenuItem>
      )}
      {controller.type >= 6000 /*&& port.type == 'AO_VOLTAGE'*/ && (
        <MenuItem value={101}>{t('exhaust_fans_ec')}</MenuItem>
      )}

      <MenuItem value={2}>{t('humidifier')}</MenuItem>
      <MenuItem value={3}>{t('dehumidifier')}</MenuItem>
      <MenuItem value={4}>{t('lights')}</MenuItem>

      {controller.type >= 6000 /*&& port.type == 'A0_VOLTAGE'*/ && (
        <MenuItem value={102}>{t('dimm_lights')}</MenuItem>
      )}

      <MenuItem value={8}>{t('co2_valve_1')}</MenuItem>
      <MenuItem value={9}>{t('co2_generator')}</MenuItem>
      <MenuItem value={6}>{t('heating_mat')}</MenuItem>
      {!not_pro && (
        <MenuItem disabled={type === 0 || type === 2} value={10}>
          {t('heater')}
        </MenuItem>
      )}
      <MenuItem value={7}>{t('chiller')}</MenuItem>
      {/*!not_pro && <MenuItem value={21}>{t('interv_1_inv')}</MenuItem>}
      {!not_pro && <MenuItem value={22}>{t('interval_2')}</MenuItem>}
      {!not_pro && <MenuItem value={23}>{t('interv_2_inv')}</MenuItem>*/}

      {[
        ['event1', 11],
        ['event2', 12],
        ['event3', 13],
        ['event4', 14],
        ['event1+2', 15],
        ['event3+4', 16],
        ['event1+2+3', 17],
        ['event1+2+3+4', 18],

        ...(!not_pro
          ? [
              ['event5', 24],
              ['event6', 25],
              ['event7', 26],
              ['event8', 27],
              ['event5+6', 28],
              ['event1-8', 30]
            ]
          : [])
      ].map((i) => (
        <MenuItem value={i[1]}>{t(i[0])}</MenuItem>
      ))}

      {[
        ['interval', 5],
        ...(!not_pro
          ? [
              ['interv_1_inv', 21],
              ['interval_2', 22],
              ['interv_2_inv', 23]
            ]
          : [])
      ].map((i) => (
        <MenuItem value={i[1]}>{t(i[0])}</MenuItem>
      ))}

      <MenuItem value={19}>{t('disabled')}</MenuItem>
      <MenuItem value={20}>{t('out_en_always')}</MenuItem>
      {showGrowBaseProWifiIrrigation &&
        [31, 32, 33, 34, 35].map((value) => (
          <MenuItem key={value} value={value}>
            {t(multiOutMapping[value])}
          </MenuItem>
        ))}
    </Select>
  );
}

export function MultiIOSelect({ type, value, controller, parent, hw_types = [], ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();

  if (type !== -1) {
    hw_types.push(multiIOHwTypeMappping[type]);
  }

  const mappingSelect = isZonable(controller)
    ? multiMultiIOHwTypeMappingSelect
    : multiIOHwTypeMappingSelect;

  for (let hw_type of hw_types) {
    if (!hw_type || !mappingSelect[hw_type]) {
      return null; // Return null if hw_type is invalid or not found in the mapping
    }
  }

  return (
    <React.Fragment>
      {other.no_label && (
        <>
          <label className={classes.label}>{hw_types.map((hw_type) => t(hw_type))}</label>
          <br />
        </>
      )}
      <Select
        className={classes.selectEmpty}
        value={value}
        {...other}
        title={t(multiIOMapping[value] ?? multiIOMapping['default'])}
      >
        {hw_types.map((hw_type) =>
          Object.values(
            mappingSelect[hw_type]
              /* filter out items for interval & event timer for industrial, we use separate loop for these
          because they are dynamic. Make sure to not to filter out accidentally other stuff by using "int" as filter */
              .filter(
                (v) => !(controller.type >= 6000 && (v.includes('int') || v.includes('event')))
              )
              .map((v) => {
                const optionValue = parseInt(
                  isZonable(controller)
                    ? Object.keys(multiIOMapping).find((key) => multiIOMapping[key] === v)
                    : Object.keys(multiOutMapping).find((key) => multiOutMapping[key] === v)
                );
                const routeUnsupported =
                  optionValue === MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE && getValveIrrigationRouteCount(controller, parent) === 0;
                if (routeUnsupported && Number(value) !== MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE) return null;
                return (
                  <MenuItem
                    key={`${hw_type}-${optionValue}`}
                    value={optionValue}
                    disabled={routeUnsupported}
                  >
                    {t(v).length > 25 ? t(v).substring(0, 25) + '...' : t(v)}
                  </MenuItem>
                );
              })
          )
        )}
        {controller.type >= 6000 &&
          hw_types.filter((hw_type) => ['digital_out', 'mixed_out'].includes(hw_type)).length >
            0 && <MenuItem value={200}>{t(`event`)}</MenuItem>}

        {controller.type >= 6000 &&
          hw_types.filter((hw_type) => ['digital_out', 'mixed_out'].includes(hw_type)).length >
            0 && <MenuItem value={400}>{t(`interval`)}</MenuItem>}
      </Select>
    </React.Fragment>
  );
}

export function MultiIOSelectIndex({
  count,
  mapping = undefined,
  selectedFuncsIndexes = [],
  smaller = false,
  ...other
}) {
  let { t } = useTranslation();
  const [mobile] = useState(isMobile);
  const classes = useStyles();
  const currentValue = other.value;

  if (count === -1) count = Object.keys(mapping ?? {}).length;

  if (count <= 0) {
    return (
      <Select className={mobile ? classes.selectIndex : classes.selectEmpty} {...other}>
        {Number.isInteger(currentValue) && (
          <MenuItem value={currentValue} disabled>
            {currentValue + 1}
          </MenuItem>
        )}
        <MenuItem value="" disabled>
          {'---'}
        </MenuItem>
      </Select>
    );
  }

  // Build list of indices, keep currentValue even if already used, hide others that are used
  const indices = [...Array(count).keys()];
  const visible = indices.filter((i) => !selectedFuncsIndexes.includes(i) || i === currentValue);

  return (
    <Select className={smaller ? classes.selectIndex : classes.selectEmpty} {...other}>
      <MenuItem value="io_index" disabled>
        {t('io_index')}
      </MenuItem>
      {Number.isInteger(currentValue) && (currentValue < 0 || currentValue >= count) && (
        <MenuItem value={currentValue} disabled>
          {t('unknown')} ({currentValue})
        </MenuItem>
      )}
      {visible.map((i) =>
        mapping !== undefined ? (
          <MenuItem key={i} value={i}>
            {t(mapping[i] ?? mapping['default'])}
          </MenuItem>
        ) : (
          <MenuItem key={i} value={i}>
            {i + 1}
          </MenuItem>
        )
      )}
    </Select>
  );
}

export function OverwrIOSelectType({ availableTypes = [], controller, parent, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();

  return (
    <React.Fragment>
      <Select className={classes.selectEmpty} {...other}>
        <MenuItem value="type" disabled>
          {t('type')}
        </MenuItem>
        {availableTypes.includes('digital_out') && (
          <MenuItem value={'digital_out'}>{t('digital_out')}</MenuItem>
        )}
        {availableTypes.includes('analog_out') && (
          <MenuItem value={'analog_out'}>{t('analog_out')}</MenuItem>
        )}
      </Select>
    </React.Fragment>
  );
}

export function OverwrIOSelectIO({
  type,
  multiIO,
  slaveAddress,
  selectedIds = [],
  controller,
  parent,
  ...other
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value="multifunctional_io" disabled>
        {t('multifunctional_io')}
      </MenuItem>
      {Object.values(multiIO)
        .filter(
          (v) =>
            multiIOHwTypeMappping[v.type_id] === type &&
            v.overwr_select === 0 &&
            !selectedIds.includes(v.id)
        )
        .map((v) => (
          <MenuItem key={v.id} value={v.id}>
            {t('multifunctional_io') + ' ' + getPhysicalIOLabel(controller, slaveAddress, v.id)}
          </MenuItem>
        ))}
    </Select>
  );
}

export function OverwrIOFuncSelect({
  controller,
  parent,
  filterByType,
  selectedFuncs = [],
  ...other
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  // Allowed labels filtered by hardware type
  const allowedLabels =
    filterByType && multiMultiIOHwTypeMappingSelect[filterByType]
      ? new Set(multiMultiIOHwTypeMappingSelect[filterByType])
      : null;

  // selectedFuncs now already represents final exclusion (non-indexable used OR indexable fully consumed)
  const excluded = new Set(selectedFuncs);

  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value="overwrite_by_function_hl" disabled>
        {t('overwrite_by_function_hl')}
      </MenuItem>
      {Object.entries(multiIOMapping).map(([key, label]) => {
        if (key === 'default' || key === '19') return null;
        if (allowedLabels && !allowedLabels.has(label)) return null;
        const numericKey = Number(key);
        const routeUnsupported =
          numericKey === MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE && getValveIrrigationRouteCount(controller, parent) === 0;
        if (routeUnsupported && Number(other.value) !== MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE) return null;
        if (excluded.has(numericKey)) return null;
        return (
          <MenuItem key={key} value={numericKey} disabled={routeUnsupported}>
            {t(label)}
          </MenuItem>
        );
      })}
    </Select>
  );
}

export function AHUControlModeSelect({ control, parent, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={0}>{t('ahu_control_mode_auto')}</MenuItem>
      <MenuItem value={1}>{t('ahu_control_mode_manual')}</MenuItem>
    </Select>
  );
}

export function EventGapSelect({ maxDays = 28, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      {Array.from({ length: maxDays }, (_, index) => (
        <MenuItem key={index} value={index}>
          {t(index === 0 ? 'every_day' : `every_${index + 1}_day`)}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * If current esp_fw and gc_fw are NOT supplied (f.e. in Recovery Mode)
 * requirement checks are surpassed! (=> ALL currently available firmware
 * options are available)
 */
export function FirmwareSelect({ versions, esp_fw, gc_fw, cont_type, ...other }) {
  const classes = useStyles();
  console.log('[fw] versions: ', versions);
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={-1} disabled>
        ---
      </MenuItem>
      {versions.map((version, i) => (
        <MenuItem
          key={version.name ?? i}
          value={i}
          disabled={
            version.name === '---' ||
            !IsFirmwareVersionSelectable(gc_fw, esp_fw, version, cont_type)
          }
        >
          {version.name}
        </MenuItem>
      ))}
    </Select>
  );
}

export function AlarmSelect({ ...other }) {
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value="all">All</MenuItem>
      {Object.entries(alarmMapping).map(([alarm_id, alarm]) => (
        <MenuItem value={alarm_id}>{alarm.name}</MenuItem>
      ))}
    </Select>
  );
}

export function HistoryResSelect({ dailyLabelKey = 'history_type_daily', ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={resolutions.recent}>{t('history_type_recent')}</MenuItem>
      <MenuItem value={resolutions.daily}>{t(dailyLabelKey)}</MenuItem>
      <MenuItem value={resolutions.full}>{t('history_type_full')}</MenuItem>
    </Select>
  );
}

export function TimeFormatSelect({ ...other }) {
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={'DD.MM.YYYY HH:mm:ss'}>17.01.2022 16:17:33</MenuItem>
      <MenuItem value={'YYYY-MM-DD HH:mm:ss'}>2022-01-17 16:17:33</MenuItem>
      <MenuItem value={'YYYY-MM-DD hh:mm:ss a'}>2022-01-17 04:17:33 PM</MenuItem>
    </Select>
  );
}

export function DelimiterSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={';'}>{t('semicolon')}</MenuItem>
      <MenuItem value={','}>{t('comma')}</MenuItem>
    </Select>
  );
}

export function DecimalSelect({ delimiter, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={','} disabled={delimiter === ','}>
        {t('comma')}
      </MenuItem>
      <MenuItem value={'.'}>{t('dot')}</MenuItem>
    </Select>
  );
}

export function TokenTypeSelect({ has_write = true, ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={'read'}>{t('Read')}</MenuItem>
      {has_write && <MenuItem value={'write'}>{t('Write')}</MenuItem>}
    </Select>
  );
}

export function ReadWriteSelect({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={'none'}>{t('None')}</MenuItem>
      <MenuItem value={'read'}>{t('Read')}</MenuItem>
      <MenuItem value={'write'}>{t('Write')}</MenuItem>
    </Select>
  );
}

export function GenericSelect({ values, value, onChange, disabled, ...other }) {
  return (
    <Select value={value} onChange={onChange} disabled={disabled ?? false} {...other}>
      {values.map((v) => (
        <MenuItem key={v['value']} value={v['value']}>
          {v['label']}
        </MenuItem>
      ))}
    </Select>
  );
}

export function DayWeekSelect({ use_week, ...other }) {
  const classes = useStyles();
  if (use_week) {
    return (
      <Select className={classes.selectEmpty} {...other}>
        {[...Array(56).keys()].map((week) => (
          <MenuItem value={7 * (1 + week)}>{1 + week}</MenuItem>
        ))}
      </Select>
    );
  } else {
    return (
      <Select className={classes.selectEmpty} {...other}>
        {[...Array(392).keys()].map((day) => (
          <MenuItem value={1 + day}>{1 + day}</MenuItem>
        ))}
      </Select>
    );
  }
}

export function DayWeekToggle({ ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  return (
    <Select className={classes.selectEmpty} {...other}>
      <MenuItem value={false}>{t('days')}</MenuItem>
      <MenuItem value={true}>{t('weeks')}</MenuItem>
    </Select>
  );
}

export function FlexSelect({ mapping, use_index = true, disabledValues = [], ...other }) {
  let { t } = useTranslation();
  const classes = useStyles();
  const entries = Array.isArray(mapping)
    ? mapping
    : Object.keys(mapping)
        .filter((key) => key !== 'default')
        .map((key) => [key, mapping[key]]);

  return (
    <Select className={classes.selectEmpty} {...other}>
      {entries.map(([value, label], i) => {
        const optionValue = use_index ? i : value;
        const isFallback = t(label) === '---';
        const disabled =
          isFallback ||
          disabledValues.some((disabledValue) => String(disabledValue) === String(optionValue));

        return (
          <MenuItem
            key={value}
            value={optionValue}
            disabled={disabled}
            style={isFallback ? { display: 'none' } : undefined}
          >
            {t(label)}
          </MenuItem>
        );
      })}
    </Select>
  );
}
